import { trans } from "@mongez/localization";
import { Form } from "@mongez/react-form";
import { useRevalidator } from "@remix-run/react";
import { addressesAtom, useAddAddress } from "core/addresses";
import { checkoutAtom } from "core/checkout";
import { createPortal, parseError } from "core/shared";
import { userAtom } from "core/user";
import { action } from "framework/config/action";
import { URLS } from "framework/config/urls";
import { Truck } from "lucide-react";
import {
  Button,
  errorToast,
  PhoneNumberInput,
  SubmitButton,
  successToast,
  TextareaInput,
  TextInput,
} from "~/design-system/components";
import { SelectAddressCityAndDistrict } from "~/modules/my-account/components/addresses/SelectAddressCityAndDistrict";
import { Portal } from "~/modules/shared/components";

export const switchToDeliveryPortal = createPortal("switch-to-delivery-portal");

export function SwitchToDeliveryPortal() {
  const user = userAtom.useValue();

  const { cart } = checkoutAtom.useValue();

  const currentAddress = cart?.shippingAddress;

  const revalidator = useRevalidator();

  const submitCheckout = action(URLS.checkout.root);

  const submitGuestCheckout = action(URLS.checkout.guest);

  const setShippingAddressForUser = async (addressId: number) => {
    const response = await submitCheckout({
      action: "setShippingAddress",
      addressId,
      shippingType: "delivery",
    });

    if (response.isOK) {
      const data = response.data?.data ?? response.data;
      if (data?.cart) {
        checkoutAtom.change("cart", data.cart);
      }
      revalidator.revalidate();
      return true;
    }

    errorToast(parseError(response));
    return false;
  };

  const setShippingAddressForGuest = async (values: any) => {
    const response = await submitGuestCheckout({
      action: "setShippingAddress",
      ...values,
      shippingType: "delivery",
    });

    if (response.isOK) {
      const data = response.data?.data ?? response.data;
      const responseCart = data?.cart;
      const newAddress = responseCart?.shippingAddress;
      checkoutAtom.merge({
        addresses: newAddress ? [newAddress] : [],
        cart: responseCart ?? cart,
      });
      revalidator.revalidate();
      return true;
    }

    errorToast(parseError(response));
    return false;
  };

  const { addAddressSubmit } = useAddAddress({
    onSuccess: async (data: any) => {
      addressesAtom.update(data.addresses);
      checkoutAtom.change("addresses", data.addresses);

      const newAddress = data.addresses?.[data.addresses.length - 1];
      if (!newAddress?.id) return;

      const ok = await setShippingAddressForUser(newAddress.id);
      if (!ok) return;

      successToast(trans("checkout.switchedToDelivery"));
      switchToDeliveryPortal.close();
    },
    onError: (error: any) => {
      errorToast(error);
    },
  });

  const handleSubmit = async (props: any) => {
    if (user.userType === "user") {
      return addAddressSubmit(props);
    }

    const ok = await setShippingAddressForGuest(props.values);
    props.form.submitting(false);
    if (!ok) return;

    successToast(trans("checkout.switchedToDelivery"));
    switchToDeliveryPortal.close();
  };

  return (
    <Portal
      portal={switchToDeliveryPortal}
      title={trans("checkout.switchToDelivery")}
      subtitle={trans("checkout.switchToDeliveryHint")}
      width={640}
    >
      <Form onSubmit={handleSubmit} className="flex flex-col gap-5">
        <TextInput
          classes={{ input: "bg-white/50" }}
          name="name"
          placeholder={trans("name")}
          defaultValue={currentAddress?.name || user.name}
          required
        />

        <PhoneNumberInput
          id="phoneNumber"
          name="phoneNumber"
          minLength={11}
          classes={{ input: "bg-white/50" }}
          label={trans("phoneNumber")}
          defaultValue={currentAddress?.phoneNumber || user.phoneNumber}
          placeholder={trans("phoneNumber")}
          className="w-full"
          inputClassName="rounded-lg py-4 px-4"
          variant="filled"
          required
        />

        <TextareaInput
          classes={{ input: "bg-white/50" }}
          name="address"
          placeholder={trans("address")}
          defaultValue={currentAddress?.address}
          required
        />

        <SelectAddressCityAndDistrict
          withDefaultValue
          excludeOther
          address={
            currentAddress && !currentAddress.isOther
              ? currentAddress
              : undefined
          }
        />

        <TextInput
          classes={{ input: "bg-white/50" }}
          name="landmark"
          placeholder={trans("landmark")}
          defaultValue={currentAddress?.landmark}
        />

        <div className="mt-4 flex items-center gap-4">
          <Button
            onClick={switchToDeliveryPortal.close}
            className="flex-1 justify-center rounded-full border bg-[#DFDFDF] px-6 py-3 font-medium hover:bg-[#d3d3d3]"
          >
            {trans("cancel")}
          </Button>
          <SubmitButton className="bg-primary-light hover:bg-primary-300 inline-flex flex-1 items-center justify-center gap-2 rounded-full border px-6 py-3 font-semibold">
            <Truck className="size-4" />
            {trans("checkout.switchToDelivery")}
          </SubmitButton>
        </div>
      </Form>
    </Portal>
  );
}
